import { useEffect } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  hasSubdomainClientId,
  extractClientIdFromSubdomain,
} from './useClientId';
import { buildClientPrefixedPath } from '../utils/url';

// Regex patterns used for path cleaning and validation
const REGEX_SPECIAL_CHARS_ESCAPE = /[.*+?^${}()|[\]\\]/g;
const MULTIPLE_SLASHES_PATTERN = /\/+/g;
const TRAILING_SLASH_PATTERN = /\/$/;
const ANY_CLIENT_ID_SEGMENT_PATTERN = /\/clientId\/[^/]+(\/|$)/g;
const HAS_ANY_CLIENT_ID_SEGMENT_PATTERN = /\/clientId\/[^/]+/;

/**
 * Escapes special regex characters in a string for safe use in regex patterns.
 */
function escapeRegexChars(str: string): string {
  return str.replace(REGEX_SPECIAL_CHARS_ESCAPE, '\\$&');
}

/**
 * Creates a regex pattern to match /clientId/${clientId} segments in a pathname.
 */
function createClientIdSegmentPattern(clientId: string): RegExp {
  const escapedClientId = escapeRegexChars(clientId);
  return new RegExp(`/clientId/${escapedClientId}(/|$)`, 'g');
}

/**
 * Creates a regex pattern to match all occurrences of /clientId/${clientId} for duplicate detection.
 */
function createDuplicateClientIdPattern(clientId: string): RegExp {
  const escapedClientId = escapeRegexChars(clientId);
  return new RegExp(`/clientId/${escapedClientId}`, 'g');
}

/**
 * Removes ALL occurrences of /clientId/${clientId} segments from a path.
 * This prevents infinite loops when paths contain multiple clientId segments.
 */
function removeAllClientIdSegments(pathname: string, clientId: string): string {
  // Remove all occurrences of /clientId/${clientId} (with optional trailing slash)
  const clientIdSegmentPattern = createClientIdSegmentPattern(clientId);
  let cleaned = pathname.replace(clientIdSegmentPattern, '/');
  // Normalize multiple consecutive slashes and ensure path starts with /
  cleaned = cleaned.replace(MULTIPLE_SLASHES_PATTERN, '/');
  if (!cleaned.startsWith('/')) {
    cleaned = `/${cleaned}`;
  }
  // If we removed everything, return root
  return cleaned === '/'
    ? '/'
    : cleaned.replace(TRAILING_SLASH_PATTERN, '') || '/';
}

/**
 * Removes ALL /clientId/* segments from a path, regardless of the clientId value.
 * This is used to clean paths that may contain any clientId segment before rebuilding.
 * Iterates until no more matches are found to handle cases with multiple clientId segments.
 */
function removeAllClientIdSegmentsGeneric(pathname: string): string {
  // Match any /clientId/{anything} segment
  // Iterate until no more matches are found (handles multiple consecutive clientId segments)
  let cleaned = pathname;
  let prevCleaned = '';
  while (cleaned !== prevCleaned) {
    prevCleaned = cleaned;
    cleaned = cleaned.replace(ANY_CLIENT_ID_SEGMENT_PATTERN, '/');
  }
  // Normalize multiple consecutive slashes and ensure path starts with /
  cleaned = cleaned.replace(MULTIPLE_SLASHES_PATTERN, '/');
  if (!cleaned.startsWith('/')) {
    cleaned = `/${cleaned}`;
  }
  // If we removed everything, return root
  return cleaned === '/'
    ? '/'
    : cleaned.replace(TRAILING_SLASH_PATTERN, '') || '/';
}

/**
 * Hook that corrects the URL for new URL structure when the route clientId
 * differs from the subdomain-extracted clientId.
 *
 * For new URL structure (dashboard.{client}.{stack}.{domain}), the subdomain
 * clientId is the source of truth. If the URL path contains a different
 * clientId, this hook redirects to the correct URL.
 *
 * This only applies to the new URL structure. Legacy URLs are not affected.
 */
export function useClientIdUrlCorrection(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const routeClientId = (params as Record<string, string | undefined>)
    ?.clientId;

  useEffect(() => {
    const hostname = window.location.hostname;
    const hasSubdomain = hasSubdomainClientId(hostname);

    if (hasSubdomain) {
      const subdomainClientId = extractClientIdFromSubdomain(hostname);

      if (!subdomainClientId) {
        return;
      }

      const expectedPrefix = `/clientId/${subdomainClientId}`;

      // Check if path has duplicate clientId segments (indicates corrupted path)
      // Regex to match all occurrences of /clientId/${subdomainClientId} in the pathname
      const duplicateClientIdSegmentPattern =
        createDuplicateClientIdPattern(subdomainClientId);
      const segmentMatches = location.pathname.match(
        duplicateClientIdSegmentPattern
      );
      const hasDuplicateSegments = segmentMatches && segmentMatches.length > 1;

      // If path already starts with correct prefix AND has no duplicates, skip redirect
      if (
        !hasDuplicateSegments &&
        (location.pathname === expectedPrefix ||
          location.pathname.startsWith(`${expectedPrefix}/`))
      ) {
        return;
      }

      // If we have duplicate segments or incorrect clientId, clean up the path
      let pathToClean = location.pathname;

      // If routeClientId exists and differs from subdomain, remove all clientId segments generically
      // This ensures we remove ALL wrong clientIds, not just routeClientId and subdomainClientId
      // Edge case: pathname could contain multiple different wrong clientIds (e.g., /clientId/wrong1/clientId/wrong2/entities)
      if (routeClientId && routeClientId !== subdomainClientId) {
        // Use generic removal to handle all possible wrong clientIds in the pathname
        pathToClean = removeAllClientIdSegmentsGeneric(pathToClean);
      } else {
        // Check if pathname contains any clientId segment that doesn't match subdomain
        // This handles race conditions where pathname updates before routeClientId param
        const hasAnyClientIdSegment =
          HAS_ANY_CLIENT_ID_SEGMENT_PATTERN.test(pathToClean);
        const hasCorrectPrefix =
          pathToClean.startsWith(expectedPrefix) ||
          pathToClean.startsWith(`${expectedPrefix}/`);

        if (hasAnyClientIdSegment && !hasCorrectPrefix) {
          // Path has clientId segments but not the correct one - remove all generically
          pathToClean = removeAllClientIdSegmentsGeneric(pathToClean);
        } else {
          // Remove all occurrences of subdomain clientId segments (handles duplicates)
          pathToClean = removeAllClientIdSegments(
            pathToClean,
            subdomainClientId
          );
        }
      }

      // Build the correct path with subdomain clientId
      const correctPath = buildClientPrefixedPath(
        pathToClean,
        subdomainClientId
      );

      // Safety check: Prevent redirect if resulting path would be same as current
      if (correctPath === location.pathname) {
        return;
      }

      // Preserve search params safely using useSearchParams
      const search = searchParams.toString();

      navigate(
        {
          pathname: correctPath,
          search: search ? `?${search}` : '',
        },
        { replace: true }
      );
    }
  }, [navigate, location.pathname, searchParams, routeClientId]);
}
